import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { Avatar, IconButton } from "@mui/material";
import React, { useMemo, useRef, useState } from "react";

import MainHeading from "../components/main-heading.component";
import { useMainApi } from "../provider/main-api.provider";
import { _patch } from "../services/api.service";
import { _notify, NotificationType } from "../services/notification.service";
import "./Profile.css";

type EditableField = "first_name" | "last_name" | "instagram_handle" | "closing_line";

type FieldConfig = {
  key: EditableField;
  label: string;
  placeholder: string;
  maxLength?: number;
  prefix?: string;
};

const FIELDS: FieldConfig[] = [
  { key: "first_name", label: "First name", placeholder: "e.g. Tarun" },
  { key: "last_name", label: "Last name", placeholder: "e.g. Batra" },
  { key: "instagram_handle", label: "Instagram handle", placeholder: "yourhandle", maxLength: 30, prefix: "@" },
  {
    key: "closing_line",
    label: "Closing line",
    placeholder: "e.g. Follow for more home-buying tips",
    maxLength: 255,
  },
];

const MAX_PICTURE_SIZE_BYTES = 0.5 * 1024 * 1024;
const ALLOWED_PICTURE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const ProfilePage = () => {
  const { me, loading, refresh } = useMainApi();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [draft, setDraft] = useState("");
  const [savingField, setSavingField] = useState<EditableField | null>(null);
  const [uploading, setUploading] = useState(false);

  const fieldValues = useMemo<Record<EditableField, string>>(
    () => ({
      first_name: me?.first_name ?? "",
      last_name: me?.last_name ?? "",
      instagram_handle: me?.profile?.instagram_handle ?? "",
      closing_line: me?.profile?.closing_line ?? "",
    }),
    [me]
  );

  const initials = useMemo(() => {
    const first = me?.first_name?.trim()?.charAt(0) ?? "";
    const last = me?.last_name?.trim()?.charAt(0) ?? "";
    return `${first}${last}`.toUpperCase();
  }, [me]);

  const startEditing = (field: EditableField) => {
    setEditingField(field);
    setDraft(fieldValues[field]);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setDraft("");
  };

  const handleDraftChange = (field: EditableField, value: string) => {
    setDraft(field === "instagram_handle" ? value.replace(/^@+/, "").replace(/\s/g, "") : value);
  };

  const saveField = async (field: EditableField) => {
    if (savingField) return;

    const trimmed = draft.trim();
    const isNameField = field === "first_name" || field === "last_name";
    const payload = { [field]: trimmed || (isNameField ? "" : null) };

    setSavingField(field);
    try {
      const response = await _patch("users/profile/", payload);
      if (response) {
        _notify(NotificationType.SUCCESS, "Profile updated");
        await refresh({ force: true });
        cancelEditing();
      }
    } catch (err) {
      console.error("Failed to update profile", err);
    } finally {
      setSavingField(null);
    }
  };

  const handleFieldKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, field: EditableField) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveField(field);
    } else if (event.key === "Escape") {
      cancelEditing();
    }
  };

  const handlePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!ALLOWED_PICTURE_TYPES.includes(file.type)) {
      _notify(NotificationType.ERROR, "Please upload a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > MAX_PICTURE_SIZE_BYTES) {
      _notify(NotificationType.ERROR, "Image must be 0.5 MB or smaller.");
      return;
    }

    const formData = new FormData();
    formData.append("profile_picture", file);

    setUploading(true);
    try {
      const response = await _patch("users/profile/", formData);
      if (response) {
        _notify(NotificationType.SUCCESS, "Profile photo updated");
        await refresh({ force: true });
      }
    } catch (err) {
      console.error("Failed to upload profile picture", err);
    } finally {
      setUploading(false);
    }
  };

  const getDisplayValue = (field: EditableField) => {
    const value = fieldValues[field];
    if (!value) return "Not set";
    return field === "instagram_handle" ? `@${value}` : value;
  };

  if (loading && !me) {
    return (
      <div className="container-fluid profile-page">
        <MainHeading title="Profile" />
        <div className="pf-loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="container-fluid profile-page">
      <MainHeading
        title="Profile"
        subTitle="Manage the details shown on the closing screen of your videos."
      />

      <section className="pf-card">
        <div className="pf-photo-section">
          <Avatar
            className="pf-avatar"
            src={me?.profile?.profile_picture ?? undefined}
            alt={initials || "Profile"}
          >
            {initials}
          </Avatar>
          <div className="pf-photo-copy">
            <h3 className="pf-card-title">Profile photo</h3>
            <p className="pf-card-subtitle">
              Your photo appears next to your name on the final screen of your videos.
            </p>
            <div className="pf-photo-actions">
              <button
                type="button"
                className="pf-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
                {uploading ? "Uploading..." : "Change photo"}
              </button>
              <span className="pf-help">JPG, PNG or WEBP · up to 0.5 MB</span>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_PICTURE_TYPES.join(",")}
            className="pf-file-input"
            onChange={handlePictureChange}
          />
        </div>

        <div className="pf-divider" />

        <div className="pf-rows">
          {FIELDS.map((field) => {
            const isEditing = editingField === field.key;
            const isSaving = savingField === field.key;

            return (
              <div className={`pf-row${isEditing ? " is-editing" : ""}`} key={field.key}>
                <div className="pf-row-body">
                  <span className="pf-row-label">{field.label}</span>

                  {isEditing ? (
                    <div className="pf-edit">
                      <div className="pf-input-wrap">
                        {field.prefix && <span className="pf-input-prefix">{field.prefix}</span>}
                        <input
                          autoFocus
                          type="text"
                          className={`pf-input${field.prefix ? " pf-input--with-prefix" : ""}`}
                          value={draft}
                          maxLength={field.maxLength}
                          placeholder={field.placeholder}
                          disabled={isSaving}
                          onChange={(event) => handleDraftChange(field.key, event.target.value)}
                          onKeyDown={(event) => handleFieldKeyDown(event, field.key)}
                        />
                      </div>
                      <div className="pf-edit-actions">
                        {field.maxLength && (
                          <span className="pf-char-count">
                            {draft.length}/{field.maxLength}
                          </span>
                        )}
                        <IconButton
                          size="small"
                          aria-label={`Save ${field.label}`}
                          onClick={() => saveField(field.key)}
                          disabled={isSaving}
                          sx={{ color: "#059669" }}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Cancel editing ${field.label}`}
                          onClick={cancelEditing}
                          disabled={isSaving}
                          sx={{ color: "#66738a" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    <span className={`pf-row-value${fieldValues[field.key] ? "" : " is-empty"}`}>
                      {getDisplayValue(field.key)}
                    </span>
                  )}
                </div>

                {!isEditing && (
                  <IconButton
                    className="pf-edit-btn"
                    aria-label={`Edit ${field.label}`}
                    onClick={() => startEditing(field.key)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};


export default ProfilePage;
